import type { Editor } from '@tiptap/core';
import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    collapsible: {
      setCollapsible: () => ReturnType;
      toggleCollapsible: () => ReturnType;
    };
  }
}

export interface CollapsibleOptions {
  persist: boolean;
  openClassName: string;
  HTMLAttributes: Record<string, any>;
}

export const Collapsible = Node.create<CollapsibleOptions>({
  name: 'collapsible',
  group: 'block',
  content: 'collapsibleSummary collapsibleContent',
  defining: true,
  isolating: true,

  addOptions() {
    return {
      persist: true,
      openClassName: 'is-open',
      HTMLAttributes: { class: 'collapsible' },
    };
  },

  addAttributes() {
    return {
      'data-open': {
        default: false,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute('data-open') === 'true',
        renderHTML: (attributes: { 'data-open': boolean }) => {
          const attrs: Record<string, any> = {
            'data-open': attributes['data-open'] ? 'true' : 'false',
          };
          return attrs;
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'collapsible' }, { tag: 'details[data-collapsible]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { 'data-open': dataOpen, ...rest } = HTMLAttributes as {
      'data-open'?: boolean;
    } & Record<string, any>;
    const attrs = mergeAttributes(
      { 'data-collapsible': 'true' },
      this.options.HTMLAttributes,
      rest,
      { 'data-open': dataOpen ? 'true' : 'false' }
    );

    if (this.options.openClassName && dataOpen) {
      attrs.class = [attrs.class, this.options.openClassName]
        .filter(Boolean)
        .join(' ');
    }

    // Render as <details> to leverage native toggle behavior.
    return ['details', attrs, 0];
  },

  addCommands() {
    return {
      setCollapsible:
        () =>
        ({ chain }: any) =>
          chain()
            .insertContent({
              type: this.name,
              attrs: { 'data-open': false },
              content: [
                { type: 'collapsibleSummary', content: [] },
                { type: 'collapsibleContent', content: [] },
              ],
            })
            .focus()
            .run(),

      toggleCollapsible:
        () =>
        ({ state, dispatch }: any) => {
          const { selection } = state;
          const $from = selection.$from;
          for (let depth = $from.depth; depth >= 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === this.name) {
              const pos = depth === 0 ? 0 : $from.before(depth);
              const tr = state.tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                'data-open': !node.attrs['data-open'],
              });
              if (dispatch) dispatch(tr);
              return true;
            }
          }
          return false;
        },
    };
  },

  addProseMirrorPlugins() {
    const name = this.name;
    const persist = this.options.persist;
    return [
      new Plugin({
        key: new PluginKey('collapsible-toggle'),
        props: {
          handleDOMEvents: {
            toggle: (view: any, event: any) => {
              console.log('view', view);
              console.log('event', event);
              const target = event.target as HTMLElement | null;
              if (
                !target ||
                target.tagName !== 'DETAILS' ||
                target.getAttribute('data-collapsible') !== 'true'
              ) {
                return false;
              }

              if (!persist) return false;

              // Map DOM <details> to the corresponding node and persist `data-open` state
              const pos = view.posAtDOM(target, 0);
              if (pos == null || pos < 0) return false;
              const $pos = view.state.doc.resolve(pos);
              for (let depth = $pos.depth; depth >= 0; depth--) {
                const node = $pos.node(depth);
                if (node.type.name === name) {
                  const nodePos = depth === 0 ? 0 : $pos.before(depth);
                  const isOpen = (target as HTMLDetailsElement).open;
                  const tr = view.state.tr.setNodeMarkup(nodePos, undefined, {
                    ...node.attrs,
                    'data-open': isOpen,
                  });
                  view.dispatch(tr);
                  return true;
                }
              }
              return false;
            },
          },
        },
      }),
    ];
  },
});

export const CollapsibleSummary = Node.create({
  name: 'collapsibleSummary',
  group: 'block',
  content: 'inline*',
  defining: true,

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'collapsible-summary',
      },
    } as { HTMLAttributes: Record<string, any> };
  },

  parseHTML() {
    return [
      {
        tag: 'summary',
        getAttrs: (element: HTMLElement) =>
          element.closest('collapsible, details[data-collapsible]')
            ? null
            : false,
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const providedStyle = (HTMLAttributes as any)?.style || '';
    const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
      style: `cursor: pointer; ${providedStyle || ''}`,
    });
    return ['summary', attrs, 0];
  },
});

export const CollapsibleContent = Node.create({
  name: 'collapsibleContent',
  group: 'block',
  content: 'block+',
  defining: true,

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'collapsible-content',
      },
    } as { HTMLAttributes: Record<string, any> };
  },

  parseHTML() {
    return [
      { tag: 'content' },
      {
        tag: 'div[data-type="collapsible-content"]',
        getAttrs: (element: HTMLElement) =>
          element.closest('collapsible, details[data-collapsible]')
            ? null
            : false,
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Keep the original tag name from input for round-tripping.
    const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes);
    // Use an inner wrapper to allow smooth CSS-only height animations via grid technique
    return [
      'content',
      attrs,
      ['div', { class: 'collapsible-content-inner' }, 0],
    ];
  },
});

export default Collapsible;
